import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table"
import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { Link } from "react-router-dom"
import api from "../../services/api"
import AdminLayout from "../../Components/Layout/AdminLayout"
import Button from "../../Components/Button"
import OrganizationFormModal from "../../Components/Modals/OrganizationFormModal"

const col = createColumnHelper()

const AdminOrganizations = () => {
  const [organizations, setOrganizations] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sorting, setSorting] = useState([{ id: "name", desc: false }])
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 })
  const [pageCount, setPageCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [columnSizing, setColumnSizing] = useState({})
  const debounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText)
      setPagination(p => ({ ...p, pageIndex: 0 }))
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [searchText])

  const loadOrganizations = useCallback(() => {
    setLoading(true)
    const params = {
      page: pagination.pageIndex + 1,
      per_page: pagination.pageSize,
    }
    if (debouncedSearch) params.search = debouncedSearch
    if (sorting.length > 0) {
      params.sort_by  = sorting[0].id
      params.sort_dir = sorting[0].desc ? "desc" : "asc"
    }

    api.getAdminOrganizations(params)
      .then(res => {
        setOrganizations(res.data.data ?? res.data)
        setPageCount(res.data.last_page ?? 1)
        setTotal(res.data.total ?? (res.data.data ?? res.data).length)
        setLoading(false)
      })
      .catch(() => {
        setOrganizations([])
        setLoading(false)
      })
  }, [pagination.pageIndex, pagination.pageSize, debouncedSearch, sorting])

  useEffect(() => { loadOrganizations() }, [loadOrganizations])

  const handleExportCsv = () => {
    const exportParams = { export: 1 }
    if (debouncedSearch) exportParams.search = debouncedSearch
    if (sorting.length > 0) {
      exportParams.sort_by  = sorting[0].id
      exportParams.sort_dir = sorting[0].desc ? "desc" : "asc"
    }
    api.getAdminOrganizations(exportParams)
      .then(res => {
        const all = res.data.data ?? res.data
        const headers = ["Name", "Slug", "Users", "Created"]
        const rows = all.map(o => [
          `"${(o.name || "").replace(/"/g, '""')}"`,
          o.slug,
          o.users_count ?? 0,
          o.created_at ? new Date(o.created_at).toLocaleDateString() : "",
        ])
        const csv = [headers, ...rows].map(r => r.join(",")).join("\n")
        const blob = new Blob([csv], { type: "text/csv" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `organizations-${new Date().toISOString().split("T")[0]}.csv`
        a.click()
        setTimeout(() => URL.revokeObjectURL(url), 100)
      })
  }

  const columns = useMemo(() => [
    col.accessor("name", {
      header: "Name",
      size: 180,
      minSize: 100,
      cell: ({ row }) => (
        <Link to={`/admin/organizations/${row.original.id}`} className="text-brand hover:underline font-medium">
          {row.original.name}
        </Link>
      ),
    }),
    col.accessor("slug", {
      header: "Slug",
      size: 200,
      minSize: 70,
      cell: ({ getValue }) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{getValue()}</code>
      ),
    }),
    col.accessor("users_count", {
      header: "Users",
      size: 90,
      minSize: 70,
      cell: ({ getValue }) => (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-circle/10 text-circle">
          {getValue() ?? 0}
        </span>
      ),
    }),
    col.accessor("created_at", {
      header: "Created",
      size: 120,
      minSize: 70,
      cell: ({ getValue }) => (
        <span className="text-xs text-gray-500">
          {getValue() ? new Date(getValue()).toLocaleDateString() : "—"}
        </span>
      ),
    }),
    col.display({
      id: "actions",
      header: "",
      size: 120,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => (
        <Link to={`/admin/${row.original.slug}/users`} className="text-xs text-brandSecondary hover:underline">
          <i className="fa fa-users mr-1" />
          View Users
        </Link>
      ),
    }),
  ], [])

  const table = useReactTable({
    data: organizations,
    columns,
    pageCount,
    columnResizeMode: "onChange",
    state: { sorting, pagination, columnSizing },
    onSortingChange: updater => {
      setSorting(updater)
      setPagination(p => ({ ...p, pageIndex: 0 }))
    },
    onPaginationChange: setPagination,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  const { pageIndex, pageSize } = pagination
  const from = total === 0 ? 0 : pageIndex * pageSize + 1
  const to = Math.min((pageIndex + 1) * pageSize, total)

  return (
    <AdminLayout
      headerActions={
        <>
          <Button onClick={() => setCreateModalOpen(true)} variant="default">
            <i className="fa fa-plus mr-2" />
            New Organization
          </Button>
          <Button onClick={handleExportCsv} variant="secondary-outline">
            <i className="fa fa-download mr-2" />
            Export CSV
          </Button>
        </>
      }
    >
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-brand leading-tight">Organizations</h1>
            <p className="text-sm text-gray-500 mt-1">Manage all organizations in the system.</p>
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-xl p-3 mb-3 shadow-sm">
          <div className="relative">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search organizations..."
              className="w-full bg-gray-50 border-0 rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
        </div>

        <div className="flex justify-between items-center mb-2 px-1 text-xs text-gray-400">
          <span>
            {total === 0 ? "No organizations" : `Showing ${from}–${to} of ${total}`}
          </span>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="max-h-[600px] overflow-auto">
            <table
              className="border-separate border-spacing-0"
              style={{ width: table.getTotalSize(), minWidth: "100%", tableLayout: "fixed" }}
            >
              <thead>
                {table.getHeaderGroups().map(hg => (
                  <tr key={hg.id} className="bg-gray-50">
                    {hg.headers.map(header => (
                      <th
                        key={header.id}
                        style={{ width: header.getSize() }}
                        className="group relative sticky top-0 z-10 bg-gray-50 px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400 select-none border-b border-gray-100"
                      >
                        {header.isPlaceholder ? null : (
                          <>
                            <div
                              className={header.column.getCanSort() ? "flex items-center gap-1 cursor-pointer hover:text-gray-600 transition-colors" : ""}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getCanSort() && (
                                <span className="text-gray-300">
                                  {{ asc: "↑", desc: "↓" }[header.column.getIsSorted()] ?? "↕"}
                                </span>
                              )}
                            </div>
                            {header.column.getCanResize() && (
                              <div
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                onClick={e => e.stopPropagation()}
                                className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none ${header.column.getIsResizing() ? "bg-brand opacity-100" : "bg-gray-300 opacity-0 group-hover:opacity-100"}`}
                              />
                            )}
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-16 text-center text-gray-400 text-sm">
                      <i className="fa fa-spinner fa-spin mr-2" />Loading…
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-16 text-center">
                      <i className="fa-regular fa-folder-open text-3xl text-gray-200 mb-3 block" />
                      <p className="font-semibold text-gray-500 text-sm">No organizations found</p>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} style={{ width: cell.column.getSize() }} className="px-4 py-2 align-middle text-sm">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pageCount > 1 && (
          <div className="flex items-center justify-between mt-4 px-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <span className="text-sm text-gray-600">
                Page {pageIndex + 1} of {pageCount}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
            <select
              value={pageSize}
              onChange={e => table.setPageSize(Number(e.target.value))}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 outline-none focus:ring-2 focus:ring-brand/30"
            >
              {[10, 25, 50, 100].map(s => (
                <option key={s} value={s}>{s} / page</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <OrganizationFormModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => { setCreateModalOpen(false); loadOrganizations() }}
      />
    </AdminLayout>
  )
}

export default AdminOrganizations
